/* eslint-disable @typescript-eslint/no-explicit-any */
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import cx from "clsx";
import { useEffect, useState } from "react";
import { FaPencilAlt } from "react-icons/fa";
import { ActionIcon, Box, Flex, Text, Textarea, Tooltip } from "@mantine/core";
import { IconMenu2 } from "@tabler/icons-react";
import { GraphQLQuery, generateClient } from "aws-amplify/api";
import { sortByReferenceIds } from "@/utils/utils";
import { updateTemplateSubsection } from "@/graphql/mutations";
import {
  UpdateTemplateSubsectionInput,
  UpdateTemplateSubsectionMutation,
  UpdateTemplateSubsectionMutationVariables,
} from "@/API";
import classes from "../TemplateContent.module.css";

const client = generateClient();

interface contentFieldsTemplateProps {
  subsection: any;
  sectionId: any;
  templateId: any;
  fetchTemplate: (templateId: string) => void;
  setFieldEdit: (subsection: any) => void;
  setModalEditFieldOpened: (opened: boolean) => void;
}

const ContentFieldsTemplate = ({
  subsection,
  setFieldEdit,
  setModalEditFieldOpened,
  fetchTemplate,
  templateId,
}: contentFieldsTemplateProps) => {
  const [subsectionEdit, setSubsectionEdit] = useState(subsection);

  const reorderFields = ({ from, to }: any) => {
    const sectionsClone = { ...subsection };
    const item = subsection?.fields.items[from];
    subsection?.fields?.items.splice(from, 1);
    subsection?.fields?.items.splice(to, 0, item);
    const idsOrder = sectionsClone?.fields?.items.map((item: any) => {
      return { name: item.name, id: item.id };
    });
    setSubsectionEdit(sectionsClone);
    changeFieldOrder(idsOrder.map((order: any) => order.id));
  };

  useEffect(() => {
    if (subsection?.fields) {
      const subsItemsOrder = sortByReferenceIds(
        subsection?.fields?.items,
        subsection?.fieldOrder,
      );
      subsection["fields"]["items"] = subsItemsOrder;
      setSubsectionEdit(subsection);
    }
  }, [subsection]);

  const changeFieldOrder = async (newFieldOrder: any) => {
    const updatedSubsectionInput: UpdateTemplateSubsectionInput = {
      id: subsection.id,
      _version: subsection._version,
      fieldOrder: newFieldOrder, // change this field to overrite in schema??
    };
    const updatedSubsectionVars: UpdateTemplateSubsectionMutationVariables = {
      input: updatedSubsectionInput,
    };
    try {
      await client.graphql<GraphQLQuery<UpdateTemplateSubsectionMutation>>({
        query: updateTemplateSubsection,
        variables: updatedSubsectionVars,
      });
      fetchTemplate(templateId);
    } catch (err) {
      console.error(err);
    }
  };

  const items = subsectionEdit?.fields?.items?.map(
    (field: any, index: number) => (
      <Draggable
        key={`draggable-field-${field.id}`}
        index={index}
        draggableId={field.id}
      >
        {(provided, snapshot) => (
          <Box
            className={cx("header-subsection", {
              [classes.itemDragging]: snapshot.isDragging,
            })}
            key={field.id}
            p={8}
            ref={provided.innerRef}
            {...provided.draggableProps}
          >
            <Flex key={field.index} flex={1} justify={"space-between"} p={8}>
              <Text flex={1} fz={14}>
                {field.name}
              </Text>
              <Flex flex={1} align={"center"} ta={"right"} justify={"flex-end"}>
                <Textarea
                  flex={1}
                  autosize
                  readOnly
                  variant="unstyled"
                  fz={14}
                  value={field.value}
                  ta={"right"}
                />
                <Tooltip label="Edit field" withArrow>
                  <ActionIcon
                    bg={"transparent"}
                    onClick={() => {
                      setFieldEdit({
                        ...field,
                        ssID: subsectionEdit.id,
                        sID: subsectionEdit.sID,
                      });
                      setModalEditFieldOpened(true);
                    }}
                  >
                    <FaPencilAlt size={14} color="black" />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Move field" withArrow>
                  <ActionIcon
                    {...provided.dragHandleProps}
                    bg={"transparent"}
                    className="btn-reorder"
                  >
                    <IconMenu2 size={18} color="black" />
                  </ActionIcon>
                </Tooltip>
              </Flex>
            </Flex>
          </Box>
        )}
      </Draggable>
    ),
  );

  return (
    <DragDropContext
      onDragEnd={({ destination, source }) => {
        reorderFields({
          from: source.index,
          to: destination?.index || 0,
        });
      }}
    >
      <Droppable droppableId="dnd-list-field" direction="vertical">
        {(provided) => (
          <div {...provided.droppableProps} ref={provided.innerRef}>
            {items}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default ContentFieldsTemplate;
